import * as _ from 'lodash'
import Dialog from '../generic/DialogAlkali'
import {
	Reacts,
	reactive,
	ElementRenderer,
	Button,
	H4,
	P,
	Div,
	UL,
	LI,
	all,
	Label,
	Checkbox,
	assign,
	not,
	Span,
	VBoolean,
	greaterOrEqual,
	Input,
} from 'alkali'
import LoadingEllipsis from 'components/generic/LoadingEllipsis'
import {
	currentAnalysisSelections,
	fixCasing,
	faqOrReadOnly,
	metaResults
} from 'model/SearchQuery'
import { whenDigitizedLoaded } from 'model/StudyCollection'
import { groupConnectivity } from 'model/GroupConnectivity'
import './GroupsNetwork.scss'
import { GroupingManagementDropdown } from './GroupingManagementDropdown'
const groupingDropdown = new GroupingManagementDropdown()

let interventionGroup
export class GroupsNetworkDialog extends Dialog('.treatment-graph') {
	get defaults() {
		return {
			id: 'groups-network-dialog',
			head: {
				titleText: 'Groups Network',
			},
		}
	}
	ready() {
		let showLabels = reactive(true)

		let noConnectedGroupings,
			connectedTooltip = '',
			connectedGroupsData,

			noDisconnectedGroups,
			disconnectedGroupsData

		async function getNetworkData() {
			await groupConnectivity.to(({ groupSets, disconnectedGroups }) => {
				connectedGroupsData = groupSets
				disconnectedGroupsData = disconnectedGroups
			})

			noConnectedGroupings = !connectedGroupsData || connectedGroupsData.every((set) => !set.groupSize)
			noDisconnectedGroups = !disconnectedGroupsData || disconnectedGroupsData.length === 0
			return;
		}

		getNetworkData().then(() => {

			this.body.append(
			Div('.network-dialog-body', [
				(this.groupsNetwork = new GroupsNetwork({
					showLabels,
				})),
				Div('.network-dialog-right-pane', [
					Div('.information-container', [
						Div('.information-header', [H4(['Additional Information'])]),
						Div(
							'.connected-groups', [
							Div('.info-body', [
								Div([
									H4(['Treatment Groupings']),
									noConnectedGroupings
									? P('.treatment-group-name', ['No treatment groupings in this network'], {title: 'No treatment groupings in this network'})
									: connectedGroupsData
										.filter((set) => set.groupSize > 0)
											.map((group) => {
												return UL({
													title: `The ${group.name.replace(/\b\w/g, (l) => l.toUpperCase())} group also includes the treatments: ${group.groupMembers.join(', ')}`,
													content:
													[
													Span('.treatment-group-name', group.name),
													LI('.treatment-group-terms', group.groupMembers.join(', ')),
											]})
										})
									]
								)
							])
						]),
						Div(
							'.disconnected-groups', [
									Div('.info-body', [
										H4(['Disconnected Groups',
										isFAQDashboard ? 
											null : 
										(
											Label([Checkbox(currentAnalysisSelections.settings.hideDisconnectedGroups), 'Hide from graph'])
										)
										]),
										noDisconnectedGroups
											? new P('.treatment-group-name', [
													'No disconnected groups in this network',
											  ])
											: new UL([
													...disconnectedGroupsData.map((group) => new LI('.group-label', group)),
											  ]),
									])
							], {
								title: noDisconnectedGroups
									? 'No disconnected groups in this network'
									: 'These groups are not connected to other groups in the network graph',
							}),
					]),
					Div(
						'.add-intervention-group-container',
						{
							display: not(faqOrReadOnly),
						},
						[
							Div('.add-intervention-group-header', [
								H4('.add-title', ['Add an Intervention Group']),
							]),
							Div('.add-intervention-body', [
								Label(['Name']),
								Input('#add-intervention-input', {
									placeholder: 'Group Name',
									onchange(e) {
										interventionGroup = this.value
									},
									onkeypress(e) {
										if (e.key == 'Enter') {
											interventionGroup = this.value
											addGroup(interventionGroup)
											this.value = ''
										}
									},
								}),
							]),
							Div('.add-intervention-footer', [
								Button('.primary.submit-group', ['Submit'], {
									onclick() {
										if (interventionGroup) {
											addGroup(interventionGroup)
											let groupInput = <HTMLInputElement>(
												document.getElementById('add-intervention-input')
											)
											groupInput.value = ''
										}
									},
								}),
							]),
						]
					),
				]),
			])
			)
		}
		)
	}
}
function addGroup(interventionGroup) {
	interventionGroup = fixCasing(interventionGroup)
	currentAnalysisSelections.analyzeAs.push({
		id: currentAnalysisSelections.analyzeAs.valueOf()?.length || 0,
		label: interventionGroup,
	})
	groupConnectivity.updated()
}

export class GroupsNetwork extends Div('.treatment-graph') {
	static LARGE_GRAPH = 30

	getGroupKey: (group) => string
	studiesForGroup: (group) => number[]
	showLabels: VBoolean
	groupSets: Reacts<[]>
	comparisons: Reacts<[]>

	protected filterGroups(groups) {
		return groups.filter((group) => !group.IsTotalPopulation && !group.IsSubGroup)
	}
	created(properties) {
		const loadingSet = []
		loadingSet.isLoading = true
		const groupSets = (this.groupSets = all([
				groupConnectivity.property('groupSets'),
				currentAnalysisSelections.settings.hideDisconnectedGroups,
				groupConnectivity.property('disconnectedGroups'),
				metaResults
			], (groupSets, hideDisconnectedGroups, disconnectedGroups) => {
				return hideDisconnectedGroups ? groupSets.filter(group => !disconnectedGroups.includes(group.name)) : groupSets
			})
			.whileResolving(loadingSet))
		const comparisons = (this.comparisons = groupConnectivity
			.property('comparisons')
			.whileResolving(loadingSet))
		this.showLabels = properties.showLabels || reactive(true)
		properties.classes = {
			'modal-dialog-wide': greaterOrEqual(
				groupSets.to((gs) => gs.length),
				GroupsNetwork.LARGE_GRAPH
			),
			'crowded-graph': not(this.showLabels),
		}
		new ElementRenderer({
			variable: all([groupSets, comparisons]),
			element: this,
			renderUpdate: _.debounce(this.renderSVG.bind(this)),
		})
	}

	async renderSVG([groupSets = [], comparisons = []]) {
		let d3 = await import('d3')
		await whenDigitizedLoaded
			const CIRCLE_RADIUS = 6
			const DENSE_GRAPH = 0.0001
			const DIMS = {
				width: 560,
				height: 500,
			}
			const WRAP_TEXT = 150
			const BODY_PADDING = 14

			let svgNode = this.querySelector('svg')
			if (svgNode) svgNode.remove()
			let ellipsis = this.querySelector('.lds-ellipsis')
			if (ellipsis) ellipsis.remove()

			let width =
				this.graphWidth || DIMS.width || this.parentNode.clientWidth - 2 * BODY_PADDING
			let height =
				this.graphHeight || DIMS.height || this.parentNode.clientHeight - 2 * BODY_PADDING

			this.showLabels.put(groupSets.length / (width * height) < DENSE_GRAPH)
			const showLabels = this.showLabels

			let domHeight = Math.max(height, Math.min(width, 160))
			let circle
			const largeGroup = groupSets.length > 4
			let svg
			switch (groupSets.length) {
				case 1:
				case 2: {
					svg = d3
						.select(this)
						.append('svg')
						.attr('width', width)
						.attr('height', domHeight)
						.attr('viewBox', '0 0 600 500')

					break
				}
				case 3:
				case 4: {
					svg = d3
						.select(this)
						.append('svg')
						.attr('width', width)
						.attr('height', domHeight)
						.attr('viewBox', '0 -40 600 500')

					break
				}
				case 5:
				case 6:
				case 7: {
					svg = d3
						.select(this)
						.append('svg')
						.attr('width', width)
						.attr('height', domHeight)
						.attr('viewBox', '0 -30 400 500')
					break
				}
				default: {
					svg = d3
						.select(this)
						.append('svg')
						.attr('width', width)
						.attr('height', domHeight)
						.attr('viewBox', '0 -130 600 650')
				}
			}
			height = 500 * Math.min(height / Math.min(width, 250), 1) - 30
			width = 500
			if (groupSets.length == 0) {
				if (groupSets.isLoading && !this.onlyFirstLetters)
					this.append(LoadingEllipsis('.network-loading'))
				return
			}
			const everything = svg.append('g')
			const zoomHandler = d3.zoom().on('zoom', (event) => {
				let transform = d3.event.transform
				transform.k = Math.max(transform.k, 0.1)
				everything.attr('transform', transform)
			})
			zoomHandler(svg)

			const force = d3.forceSimulation()
			const sidesForce = (alpha) => {
				for (const node of groupSets) {
					node.vx += (Math.max(-node.x, 0) - Math.max(node.x - width, 0)) * alpha
					node.vy += (Math.max(-node.y, 0) - Math.max(node.y - height, 0)) * alpha
					if (node.connectedTo.length == 0 && node.x < (width * 3) / 4) {
						// disconnected groups will be pushed to the right
						node.vx += ((width * 3) / 4 - node.x) / 10
					}
				}
			}

			force
				.force('center', d3.forceCenter(width / 2, height / 2))
				.force(
					'charge',
					d3
						.forceManyBody()
						.strength(-15)
						.distanceMax(width / 2 - (CIRCLE_RADIUS * 2 + 8))
				)
				.force(
					'link',
					d3.forceLink(comparisons).distance(300 - Math.min(groupSets.length * 3, 300))
				)
				.force('collide', d3.forceCollide(50))
				.force('sides', sidesForce)
				.nodes(groupSets)
				.on('tick', () => {
					links
						.attr('x1', (d) => d.source.x)
						.attr('y1', (d) => d.source.y)
						.attr('x2', (d) => d.target.x)
						.attr('y2', (d) => d.target.y)

					circle.attr('cx', (d) => d.x).attr('cy', (d) => d.y)

					if (text) {
						text.attr('x', (d) => d.x).attr('y', (d) => d.y)
					}
				})
				.on('end', function () {
					if (showLabels.valueOf()) {
						updateText()
					}
				})

			// add labels once the animation finishes
			let text
			const alignThreshold = 0.54 * width
			const appendText = () => {
				text = nodes.append('svg:text')
				text
					.attr('x', (data) => data.x)
					.attr('y', (data) => data.y)
					.attr('class', (data) => (data.disabled ? 'disabled' : ''))
					.attr('dx', (data) =>
						data.x > alignThreshold
							? this.onlyFirstLetters
								? '1em'
								: '2em'
							: this.onlyFirstLetters
								? '-1em'
								: '-2em'
					)
					.attr('dy', this.onlyFirstLetters ? '1em' : '-1.5em')
					.attr('text-anchor', (data) => (data.x > alignThreshold ? 'end' : 'start'))
					.text((data) => (this.onlyFirstLetters ? data.name.slice(0, 3) : data.name))
				if (showLabels.valueOf()) {
					setTimeout(() => {
						updateText()
						for (let entity of [circle, text]) {
							entity.append('title')
								.text(data => {
									if (data.groupSize > 0 && !data.description.includes(','))
										return data.name + ': ' + data.groupMembers.join(', ')
									else
										return data.description
								})
						}
					})
				}
				text.on('click', (nodeData) => {
					window.event.stopPropagation()
					if (faqOrReadOnly.valueOf()) return
					groupingDropdown.checkConnections(nodeData.connectedTo.length > 0 ? false : true)
					groupingDropdown.setGrouping(nodeData.name)
					groupingDropdown.show(window.event)
				})
			}
			let textWrapped = false
			const updateText = () => {
				if (!textWrapped) {
					textWrapped = true
					wrap(text, WRAP_TEXT)
				}
				setTimeout(anchorTextHorizontally)
			}
			const anchorTextHorizontally = () => {
				text
					.attr('x', (data) => data.x)
					.attr('dx', (data) => (data.x > alignThreshold ? '2em' : '-2em'))
				// left justify any nested tspans
				anchorTSpans()
				setTimeout(alignText)
			}
			const anchorTSpans = () => {
				text.each(function (data, i, nodes) {
					const cur = nodes[i]
					const x = cur.getAttribute('x')
					const dx = cur.getAttribute('dx')
					d3.select(this).selectAll('tspan').attr('x', x).attr('dx', dx)
				})
			}
			let aligned = false
			const alignText = () => {
				svgNode = this.querySelector('svg')
				if (!svgNode) return
				const {
					left: svgLeft,
					right: svgRight,
					width: svgWidth,
				} = svgNode.getBoundingClientRect()
				text.each(function (data) {
					const { right } = this.getBoundingClientRect()
					const node = d3.select(this)
					// if it overflows right, justify flush right
					if (right > svgRight) {
						node.attr('x', svgWidth)
						node.attr('dx', 0)
						node.attr('text-anchor', 'end')
					}
					// if it overflows left, justify flush left
					const { left } = this.getBoundingClientRect()
					if (left < svgLeft) {
						node.attr('x', 0)
						node.attr('dx', 0)
						node.attr('text-anchor', 'start')
					}
				})
				anchorTSpans()
				aligned = true
			}
			setTimeout(appendText, 40)

			this.showLabels.subscribe(
				_.after(2, (e) => {
					if (e.value()) {
						setTimeout(updateText, 100)
					}
				})
			)

			const label = this.querySelector('label')
			if (label) label.remove()

			const links = everything
				.selectAll('.link')
				.data(comparisons)
				.enter()
				.append('line')
				.attr('class', (data) => (data.disabled ? 'disabled link' : 'link'))

			// weight each edge with number of groups
			links.each(function (linkData, index) {
				const studyCount = linkData.source.groupCount
				const widthProp = `${Math.min(10, studyCount)}px`

				d3.select(this).call(function (linkElement) {
					linkElement.style('stroke-width', widthProp)
				})
			})

			const nodes = everything.selectAll('.node').data(groupSets).enter()

			circle = nodes
				.append('circle')
				.attr('class', (data) => (data.disabled ? 'node disabled' : 'node'))
				.attr('r', CIRCLE_RADIUS)

			circle.on('click', (nodeData) => {
				window.event.stopPropagation()
				if (faqOrReadOnly.valueOf()) return
				groupingDropdown.setGrouping(nodeData.name)
				groupingDropdown.show(window.event)
			})
			circle.on('mouseover', function (nodeData) {
				/*
				 * On mouseover, highlight connections between node and its neighbors
				 * If the graph is crowded and the labels are hidden,
				 * hovering on a node should cause the neighboring nodes' labels to appear.
				 */

				const toConnectedNode = (node) => ({ source, target }) => {
					return node === source ? target : node === target ? source : null
				}

				const nodesAreConnected = (nodeA, nodeB) => {
					return !!comparisons.filter((cnx) => {
						return (
							(cnx.source === nodeA && cnx.target === nodeB) ||
							(cnx.source === nodeB && cnx.target === nodeA)
						)
					}).length
				}

				const isEdgeOf = (node) => (line) => {
					return (
						(node.x === line.source.x && node.y === line.source.y) ||
						(node.x === line.target.x && node.y === line.target.y)
					)
				}

				const nodeEl = this

				// select all edges connected to this node
				// and highlight them
				svg
					.selectAll('line')
					.filter(isEdgeOf(nodeData))
					.raise()
					.attr('class', (data) => (data.disabled ? 'disabled highlight' : 'highlight'))

				// for each connected node
				// set its label element's class to 'highlighted-node-label'
				const networkCircles = svg
					.selectAll('circle')
					.filter((data) => nodesAreConnected(data, nodeData))

				networkCircles.raise()

				const node = d3.select(nodeEl)

				// raise hovered node over rest of network
				node.raise()

				// raise all the text above circles and lines
				svg.selectAll('text').raise()

				svg
					.selectAll('text')
					.filter((data) => nodesAreConnected(data, nodeData))
					.raise()
					.attr('class', 'highlighted-node-label')

				// moves target SVG element to end of SVG doc,
				// effectively keeping it on the top layer.
				// This is important for busy graphs.
				// note: .raise() is the 'idiomatic' d3 method way to
				// bring svg child to top of SVG hierarchy, but
				// it just does a parent.appendChild.
				svg
					.selectAll('text')
					.filter((data) => data === nodeData)
					.raise()
					.attr('class', 'highlighted-node-label')
					.attr('class', 'focused')
			})

			circle.on('mouseout', function (nodeData) {
				links.attr('class', (data) => (data.disabled ? 'disabled link' : 'link'))

				text && text.classed('highlighted-node-label', false).classed('focused', false)
			})

			function resize() {
				;(width = window.innerWidth), (height = window.innerHeight)
				svg.attr('width', width).attr('height', height)
				force.size([width, height]).resume()
			}

			function wrap(text, len) {
				text.each(function () {
					const text = d3.select(this)
					if (this.getComputedTextLength() <= len) return
					const x = text.attr('x')
					const dx = text.attr('dx')
					const words = text.text().split(/\s+/)
					const dy = parseFloat(text.attr('dy'))
					const lineHeight = 1.1 // ems
					let line = [],
						lineNumber = 0,
						tspan = text.text(null).append('tspan').attr('x', x).attr('dx', dx)
					for (const word of words) {
						line.push(word)
						tspan.text(line.join(' '))
						if (line.length > 1 && tspan.node().getComputedTextLength() > len) {
							line.pop()
							tspan.text(line.join(' '))
							line = [word]
							lineNumber++
							tspan = text
								.append('tspan')
								.attr('x', x)
								.attr('dx', dx)
								.attr('dy', lineHeight + 'em')
								.text(word)
						}
					}
					// shift upward to compensate for added lines
					text.attr('dy', dy - lineNumber * lineHeight + 'em')
				})
			}
	}
}
